import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../ds';

/**
 * Toast — the "記録しました" confirmation from the design.
 *
 * Sits above the bottom nav, fades up, and clears itself. Tone follows the
 * design system's voice: it confirms, it never congratulates loudly.
 */

type Tone = 'success' | 'danger';

type ToastPayload = { title: string; message?: string; tone?: Tone };

type ToastState = {
  show: (payload: ToastPayload) => void;
  /** Convenience for the common "saved something" case. */
  showSaved: (message?: string) => void;
};

const ToastContext = createContext<ToastState | null>(null);

const VISIBLE_MS = 2600;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastPayload | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((payload: ToastPayload) => {
    window.clearTimeout(timer.current);
    setToast(payload);
    timer.current = window.setTimeout(() => setToast(null), VISIBLE_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const value = useMemo<ToastState>(
    () => ({ show, showSaved: (message) => show({ title: '記録しました', message }) }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? <ToastView {...toast} /> : null}
    </ToastContext.Provider>
  );
}

function ToastView({ title, message, tone = 'success' }: ToastPayload) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 'calc(98px + env(safe-area-inset-bottom, 0px))',
        zIndex: 60,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        animation: 'fadeUp .24s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          width: '100%',
          maxWidth: 480,
          background: tone === 'danger' ? 'var(--clay-700)' : 'var(--sage-600)',
          color: '#fff',
          padding: '13px 16px',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <Icon name={tone === 'danger' ? 'AlertCircle' : 'CheckCircle2'} size={20} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>{title}</div>
          {message ? <div style={{ fontSize: 11, opacity: 0.85 }}>{message}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function useToast(): ToastState {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
